import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useUserStore } from '@/stores/user-store'
import { listConversations } from '@/services/messages'

const REFRESH_MS = 30_000

// Shared across every component that uses it, so the nav badge and the clients list agree
// and only one timer polls no matter how many are mounted.
const total = ref(0)
const byClientId = ref<Record<number, number>>({})
let users = 0
let timer: ReturnType<typeof setInterval> | null = null

/**
 * Unread chat messages for the signed-in coach. Inactive in the admin panel and in
 * "view as coach": the messaging endpoints always run as the real caller, so an admin
 * would only see their own conversations.
 */
export function useUnreadMessages() {
  const userStore = useUserStore()
  const enabled = computed(
    () => userStore.panelRole === 'COACH' && userStore.hasCoachRole && !userStore.isViewingAsCoach,
  )

  const refresh = async () => {
    if (!enabled.value || document.visibilityState !== 'visible') return
    try {
      const rows = await listConversations()
      const map: Record<number, number> = {}
      let sum = 0
      for (const r of rows) {
        if (r.myRole !== 'COACH') continue
        sum += r.unreadCount
        if (r.unreadCount > 0) map[r.clientId] = r.unreadCount
      }
      byClientId.value = map
      total.value = sum
    } catch {
      // A badge is not worth a toast; the next tick tries again.
    }
  }

  onMounted(() => {
    users++
    refresh()
    if (!timer) timer = setInterval(refresh, REFRESH_MS)
    window.addEventListener('focus', refresh)
  })

  onBeforeUnmount(() => {
    users--
    window.removeEventListener('focus', refresh)
    if (users === 0 && timer) {
      clearInterval(timer)
      timer = null
    }
  })

  return {
    enabled,
    total: computed(() => (enabled.value ? total.value : 0)),
    unreadForClient: (clientId: number) => (enabled.value ? byClientId.value[clientId] || 0 : 0),
    refresh,
  }
}
